import React, { useMemo, useState } from 'react'
import { TriangleAlert } from 'lucide-react'
import { useTranslation } from 'i18n'
import type { ProductScanConfig } from '../config'
import type { CameraUnavailableReason, ProductScanState } from '../useProductScan'
import { createDecoder } from '../decoder/create-decoder'
import { useScanLoop } from '../useScanLoop'
import ManualEntryForm from './ManualEntryForm'
// @ts-expect-error SCSS modules are untyped in this project
import * as styles from '../product-scan.module.scss'

// zxing-wasm's format names (BARCODE_FORMATS) — this step only cares about retail codes, same
// set the old BarcodeDetector/@zxing/browser path requested.
const SUPPORTED_FORMATS = ['UPCA', 'UPCE', 'EAN13', 'EAN8'] as const

export type ScannerScreenProps = {
  config: ProductScanConfig
  state: ProductScanState
  cameraUnavailableReason: CameraUnavailableReason
  stream: MediaStream | null
  isOnScreen?: boolean
  onDecoded: (code: string) => void
  onManualSubmit: (code: string) => void
  onRetry: () => void
}

// One camera-viewport component covers both the live "scanning" state and the "not-matched"
// screen (design 4), which keeps the same viewport visible with an invalid-scan pill overlaid.
const ScannerScreen = ({
  config,
  state,
  cameraUnavailableReason,
  stream,
  isOnScreen = true,
  onDecoded,
  onManualSubmit,
  onRetry,
}: ScannerScreenProps) => {
  const { t } = useTranslation('productScan')
  const [showManualEntry, setShowManualEntry] = useState(false)
  const prefersReducedMotion =
    typeof window !== 'undefined' &&
    window.matchMedia?.('(prefers-reduced-motion: reduce)').matches

  const isLive = state === 'scanning' || state === 'checking'
  // Player scrolled the step off-screen — pause the decode loop without tearing the stream
  // down, so the camera light goes off and the phone stops paying for decodes but scrolling
  // back resumes instantly with no new permission prompt (BR-5571 §2.4).
  const active = isLive && isOnScreen

  // One decoder instance for the component's lifetime — createDecoder() picks WorkerDecoder
  // (decode off the main thread) when Workers are available, WasmDecoder otherwise. Never the
  // BarcodeDetector path: iOS Safari still doesn't implement it (see decoder/create-decoder.ts).
  const decoder = useMemo(() => createDecoder(), [])

  const { videoRef, error: decodeError } = useScanLoop({
    decoder,
    formats: SUPPORTED_FORMATS,
    stream,
    active,
    onDetected: onDecoded,
  })

  if (state === 'camera-denied') {
    const copy: Record<Exclude<CameraUnavailableReason, null>, [string, string]> = {
      denied: [t('cameraDeniedHeadline'), t('cameraDeniedBody')],
      dismissed: [t('cameraDismissedHeadline'), t('cameraDismissedBody')],
      'no-camera': [t('cameraMissingHeadline'), t('cameraMissingBody')],
    }
    const [headline, body] = copy[cameraUnavailableReason || 'dismissed']

    return (
      <div className={styles.step}>
        <h2 className={styles.headline}>{headline}</h2>
        <p className={styles.introCopy}>{body}</p>
        <ManualEntryForm onSubmit={onManualSubmit} />
      </div>
    )
  }

  return (
    <div className={styles.step}>
      <h2 className={styles.headline}>{config.scannerTitle}</h2>

      <div
        className={styles.viewportWrap}
        role="img"
        aria-label={t('cameraViewportLabel')}
      >
        <video ref={videoRef as React.RefObject<HTMLVideoElement>} className={styles.video} muted playsInline />
        <div className={`${styles.scanCorners} ${prefersReducedMotion ? '' : styles.animated}`}>
          <span /><span /><span /><span />
        </div>
        {state === 'not-matched' ? (
          <div className={`${styles.pill} ${styles.invalid}`}>
            <TriangleAlert size={14} aria-hidden="true" />
            {config.invalidScanPillText}
          </div>
        ) : (
          <div className={styles.pill}>{config.scannerHelperText}</div>
        )}
      </div>

      {state !== 'not-matched' && (
        <p className={styles.introCopy}>{config.scannerInstructions}</p>
      )}

      {state === 'not-matched' && (
        <>
          <h2 className={styles.headline}>{config.invalidScanHeadline}</h2>
          <p className={styles.introCopy}>{config.invalidScanBody}</p>
          <button type="button" className={styles.cta} onClick={onRetry}>
            {config.retryCtaText}
          </button>
        </>
      )}

      {/* Wasm blocked (CSP/ad blocker/network) or camera unusable: say so and put manual entry in
          reach instead of leaving a black viewfinder that never decodes. */}
      {isLive && decodeError && (
        <p role="alert" className={styles.scannerError}>
          {t('scannerErrorBody')}
        </p>
      )}

      {isLive && !decodeError && (
        <button
          type="button"
          className={styles.linkButton}
          onClick={() => setShowManualEntry(v => !v)}
        >
          {t('manualEntryToggle')}
        </button>
      )}
      {isLive && (showManualEntry || decodeError) && <ManualEntryForm onSubmit={onManualSubmit} />}
    </div>
  )
}

export default ScannerScreen
